import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AsbuiltExportFormat } from '../../../../../viewer/geodata/asbuilt-geometry/asbuilt-geometry';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { useOptionalUserState } from '../../../../state/user-state/user-state';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { InfoBox } from '../../../info-box/info-box';
import { Stack } from '../../../stack/stack';
import { SkyviewContext } from '../../skyview-state';

type State = 'Idle' | 'ExportingLandXml' | 'ExportingDesign' | 'Error';

interface AsbuiltExportDialogProps {
  onClose: () => void;
}

const AsbuiltExportDialog = (props: AsbuiltExportDialogProps) => {
  const { skyview } = useContext(SkyviewContext);
  const userState = useOptionalUserState();
  const hasDesignToolAccess = userState?.hasDesignToolAccessForCurrentProject() ?? false;

  const { t } = useTranslation();
  const [state, setState] = React.useState<State>('Idle');

  const exportAsbuilt = async (format: AsbuiltExportFormat) => {
    try {
      switch (format) {
        case 'LandXml':
          setState('ExportingLandXml');
          await skyview.asbuiltGeometry.exportAsbuiltAsLandXml();

          props.onClose();
          break;
        case 'SkymapDesign': {
          setState('ExportingDesign');
          const assetName = await skyview.asbuiltGeometry.exportAsbuiltAsSkymapDesign();

          publish(SubscriptionTopic.ToastrMessage, {
            message: t('exportDialog.exportAsDesignSuccessful', {
              ns: 'skyviewAsbuilt',
              assetName,
            }),
            type: 'success',
          });

          props.onClose();
          break;
        }
      }
    } catch (err) {
      setState('Error');
    }
  };

  return (
    <>
      <Dialog
        closeIcon={false}
        closeOnDimmerClick={false}
        maxHeight={false}
        width={600}
        onClose={props.onClose}
      >
        {{
          header: t('exportDialog.title', { ns: 'skyviewAsbuilt' }),
          content: (
            <Stack spacing={0.5}>
              <InfoBox color="yellow">{t('exportDialog.info', { ns: 'skyviewAsbuilt' })}</InfoBox>

              {state === 'Error' && (
                <InfoBox color="red">{t('unexpectedError', { ns: 'common' })}</InfoBox>
              )}
            </Stack>
          ),
          footer: {
            left: (
              <Button
                color="secondary"
                disabled={state === 'ExportingLandXml'}
                leftIcon={{ icon: ['fad', 'download'] }}
                loading={state === 'ExportingLandXml'}
                variant="contained"
                onClick={() => exportAsbuilt('LandXml')}
              >
                {t('exportDialog.exportAsLandXmlButton', { ns: 'skyviewAsbuilt' })}
              </Button>
            ),
            right: (
              <>
                <Button
                  color="primary"
                  disabled={
                    state === 'ExportingDesign' ||
                    state === 'ExportingLandXml' ||
                    !hasDesignToolAccess
                  }
                  loading={state === 'ExportingDesign'}
                  variant="contained"
                  onClick={() => exportAsbuilt('SkymapDesign')}
                >
                  {t('exportDialog.exportAsDesignButton', { ns: 'skyviewAsbuilt' })}
                </Button>
                <Button
                  disabled={state === 'ExportingDesign' || state === 'ExportingLandXml'}
                  variant="text"
                  onClick={props.onClose}
                >
                  {t('cancel', { ns: 'common' })}
                </Button>
              </>
            ),
          },
        }}
      </Dialog>
    </>
  );
};

export { AsbuiltExportDialog };
